/**
 * MarketplaceWorkflowDeployFooter — the v3 footer (workflow-name field +
 * deploy split button + warm-pool menu). Atomic-ish so both the in-
 * project detail modal (MarketplaceDetailModal in v3) and the public
 * wizard modal (MarketplaceDeployWizardModal after a project is picked)
 * render the SAME widget instead of two divergent copies.
 *
 * Owns the name input state, the existing-names fetch (used for instant
 * collision validation), and the split button + warm-pool dropdown.
 * Surfaces deploy via the onDeploy(opts) callback so the parent decides
 * what happens on success (toast, navigate, close modal).
 *
 * Required props:
 *   entry                  — { slug, ... } template; seeds the name input
 *   projectId              — for GET /projects/:id/workflows (existing
 *                            names for collision check)
 *   projectName            — used in the help text + deploy button label
 *   onDeploy({ workflowName, warm }) — async; parent runs the deploy POST
 *   deploying              — disable state from parent (true while POST
 *                            is in flight)
 *
 * The local validation runs synchronously against the cached list of
 * the project's workflow names. The server runs the same uniqueness
 * check on submit and 409s on race with a suggested alternative —
 * displayed by the parent's toast.
 */

import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { IonIcon } from '@ionic/react';
import {
  cloudUploadOutline,
  chevronDownOutline,
  flashOutline,
} from 'ionicons/icons';
import api from '../../services/api';
import Field from '../../components/atoms/Field/Field';
import { validateWorkflowName, suggestAvailableName } from '../../utils/workflow-name';
import { AGENT_OPTIONS, parseAgentOption, findAgentOptionValue } from '../../utils/agent-options';

const MarketplaceWorkflowDeployFooter = ({
  entry,
  projectId,
  projectName,
  projectAiAgent = '',
  projectAiModel = '',
  onDeploy,
  deploying = false,
}) => {
  const [name, setName] = useState('');
  const [nameDirty, setNameDirty] = useState(false);
  const [existingNames, setExistingNames] = useState([]);
  // Triggered on first Deploy-click. Used to defer the red "agent
  // required" tint until the user actually attempts a deploy — we
  // don't want a freshly-opened modal flashing red on first paint.
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Drop the "Project default" sentinel from the dropdown entirely.
  // Showing it added an extra cognitive step ("what IS the project
  // default?") and the dropdown still rendered the literal string
  // "Project default" when the project's agent/model wasn't in our
  // curated shortlist — pure visual debt. Instead, we surface the
  // real option list and pre-select the option matching the project's
  // configured default (or leave nothing selected if the project has
  // no default — gates the Deploy button until the user picks).
  const projectHasAgent = !!projectAiAgent;
  const displayOptions = useMemo(
    () => AGENT_OPTIONS.filter((o) => o.value !== ''),
    [],
  );

  // Resolve the project's configured agent/model to one of our curated
  // dropdown options. Handles both forms the project row might store:
  //   - short alias ("opus-4.6") — what the marketplace modal writes
  //   - full SDK ID ("claude-sonnet-4-5-20250929") — what Project
  //     Settings → AI Settings writes
  // findAgentOptionValue falls back to `<agent>:auto` if the specific
  // model isn't in our shortlist, so users always see the right
  // vendor pre-selected instead of "Pick an agent…".
  const projectDefaultValue = useMemo(
    () => findAgentOptionValue({ aiAgent: projectAiAgent, aiModel: projectAiModel }, displayOptions),
    [projectAiAgent, projectAiModel, displayOptions],
  );

  // Combined agent + model selection. Initialize from the project's
  // resolved default; if the project has no agent at all the gate
  // below fires until the user picks one.
  const [agentOption, setAgentOption] = useState(projectDefaultValue);

  // Project default changing after the modal opened (rare — only if
  // the parent swaps projects without remounting): re-seed.
  const lastSeededRef = useRef(projectDefaultValue);
  useEffect(() => {
    if (projectDefaultValue !== lastSeededRef.current) {
      lastSeededRef.current = projectDefaultValue;
      if (projectDefaultValue) setAgentOption(projectDefaultValue);
    }
  }, [projectDefaultValue]);

  // Deploy gate. Two ways to satisfy: the project has a default we
  // pre-selected, OR the user has explicitly picked an option.
  const agentRequired = !agentOption;

  // Fetch the project's existing workflow names so we can seed a non-
  // colliding default and give instant offline validation feedback.
  // Best-effort — if the request fails (or hasn't fired yet because the
  // project was just created) we fall back to server-only collision
  // checks and the wizard's 409 toast handler.
  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    api.get(`/projects/${projectId}/workflows`)
      .then((res) => {
        if (cancelled) return;
        const names = Array.isArray(res)
          ? res.map((w) => w.workflowType).filter(Boolean)
          : [];
        setExistingNames(names);
      })
      .catch(() => {
        if (!cancelled) setExistingNames([]);
      });
    return () => { cancelled = true; };
  }, [projectId]);

  // Seed the input from the template slug; auto-suffix on collision.
  // Don't clobber a value the user has already started typing.
  useEffect(() => {
    if (!entry?.slug) return;
    if (nameDirty) return;
    const seeded = suggestAvailableName(entry.slug, existingNames) || entry.slug;
    setName(seeded);
  }, [entry?.slug, existingNames, nameDirty]);

  const validation = useMemo(
    () => validateWorkflowName(name, existingNames),
    [name, existingNames]
  );

  const handleDeploy = useCallback((opts) => {
    if (deploying || !entry) return;
    if (!validation.valid) return;
    // Project has no default AND user hasn't picked → flag the agent
    // select red. We STILL call onDeploy so the parent modal can flag
    // its own validation (missing integrations) on the SAME click —
    // the parent is the one that ultimately aborts when something's
    // wrong. Without this, only the first failing layer would flash
    // and the user would have to click Deploy twice to see everything
    // that's wrong.
    if (!projectAiAgent && !agentOption) {
      setSubmitAttempted(true);
    }
    const { aiAgent, aiModel } = parseAgentOption(agentOption);
    onDeploy?.({
      ...(opts || {}),
      workflowName: name,
      ...(aiAgent ? { aiAgent } : {}),
      ...(aiModel ? { aiModel } : {}),
      // Surface the "agent isn't picked yet" signal up to the parent
      // so it can short-circuit the deploy without needing to peek at
      // its own state. Stays an opaque boolean — the parent treats
      // this as another reason to abort, same as missing integrations.
      _agentMissing: !projectAiAgent && !agentOption,
    });
  }, [deploying, entry, onDeploy, name, validation, agentOption, projectAiAgent]);

  // Warm-pool dropdown — toggled via the chevron next to the main CTA.
  const [warmMenuOpen, setWarmMenuOpen] = useState(false);
  const warmMenuRef = useRef(null);
  useEffect(() => {
    if (!warmMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (warmMenuRef.current && !warmMenuRef.current.contains(e.target)) {
        setWarmMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setWarmMenuOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [warmMenuOpen]);

  const deployLabel = projectName ? `Deploy to ${projectName}` : 'Deploy';

  return (
    <>
      <div className="mkt-modal-name-label-row">
        <label className="mkt-modal-name-label" htmlFor="mkt-deploy-name-input">
          Workflow name
        </label>
        <p
          id="mkt-deploy-name-help"
          className={`mkt-modal-name-help ${validation.valid ? 'is-valid' : (name ? 'is-invalid' : '')}`}
          aria-live="polite"
        >
          {!name
            ? ''
            : validation.valid
              ? `Available — will deploy to ${projectName || 'this project'}`
              : (
                <>
                  {validation.reason || 'Pick a different name'}
                  {validation.suggestion && (
                    <>
                      {' — try '}
                      <button
                        type="button"
                        className="mkt-modal-name-suggestion"
                        onClick={() => { setName(validation.suggestion); setNameDirty(true); }}
                      >
                        {validation.suggestion}
                      </button>
                    </>
                  )}
                </>
              )}
        </p>
      </div>
      <div
        className={`mkt-modal-name-row ${validation.valid ? 'is-valid' : (name ? 'is-invalid' : '')}`}
        ref={warmMenuRef}
      >
        <Field
          id="mkt-deploy-name-input"
          type="text"
          className="mkt-modal-name-input"
          value={name}
          onChange={(e) => {
            setName(e.target.value);
            setNameDirty(true);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && validation.valid && !deploying) {
              e.preventDefault();
              handleDeploy();
            }
          }}
          placeholder={entry?.slug || 'workflow-name'}
          aria-invalid={!validation.valid && !!name}
          aria-describedby="mkt-deploy-name-help"
          autoComplete="off"
          spellCheck="false"
          disabled={deploying}
          status={name ? (validation.valid ? 'ok' : 'error') : null}
        />
        {/* Compact agent+model dropdown — lives in the same flex row
            as the name input and the Deploy button so the deploy
            footer stays a single line on wide screens. Native <select>
            sizes to its largest option label; the CSS clamps width so
            "Claude · Sonnet 4.6 (auto)" stays readable without taking
            half the row. Title attribute echoes the current label for
            anyone hovering when the select is collapsed. */}
        <select
          className={`mkt-field mkt-field--select ${agentRequired && submitAttempted ? 'is-required' : ''}`}
          value={agentOption}
          onChange={(e) => {
            setAgentOption(e.target.value);
            // Once the user picks something, dismiss the error tint —
            // they've addressed it, no reason to keep the red border.
            if (e.target.value) setSubmitAttempted(false);
          }}
          disabled={deploying}
          aria-label="AI agent for this workflow"
          aria-invalid={agentRequired}
          title={displayOptions.find((o) => o.value === agentOption)?.label || 'Pick an agent'}
        >
          {agentRequired && (
            // Placeholder shown only when project has no default AND
            // user hasn't picked. Empty value keeps the form invalid
            // (Deploy stays disabled until they pick a real option).
            <option value="" disabled>Pick an agent…</option>
          )}
          {displayOptions.map((opt) => (
            <option key={opt.value || 'default'} value={opt.value}>{opt.label}</option>
          ))}
        </select>
        <div className="mkt-modal-deploy-split">
          <button
            type="button"
            className="mkt-modal-deploy mkt-modal-deploy--main"
            onClick={() => handleDeploy()}
            disabled={deploying || !validation.valid}
            aria-label={deployLabel}
            title={!validation.valid ? 'Pick a valid name' : undefined}
          >
            <IonIcon icon={cloudUploadOutline} aria-hidden="true" />
            {deploying ? 'Deploying…' : 'Deploy'}
          </button>
          <button
            type="button"
            className="mkt-modal-deploy mkt-modal-deploy--chevron"
            onClick={() => setWarmMenuOpen((v) => !v)}
            disabled={deploying || !validation.valid}
            aria-label="Deploy options"
            aria-haspopup="menu"
            aria-expanded={warmMenuOpen}
          >
            <IonIcon icon={chevronDownOutline} aria-hidden="true" />
          </button>
        </div>

        {warmMenuOpen && (
          <div className="mkt-modal-warm-menu" role="menu">
            <div className="mkt-modal-warm-menu-header">
              <IonIcon icon={flashOutline} aria-hidden="true" />
              <span>Warm pool</span>
            </div>
            <p className="mkt-modal-warm-menu-help">
              Skip ~60s cold start by keeping Fargate tasks pre-warmed.
              Paid feature — provisioning takes ~60s after deploy.
            </p>
            <ul className="mkt-modal-warm-menu-list">
              {[1, 2, 3, 4, 5].map((n) => (
                <li key={n}>
                  <button
                    type="button"
                    role="menuitem"
                    className="mkt-modal-warm-menu-item"
                    onClick={() => {
                      setWarmMenuOpen(false);
                      handleDeploy({ warm: n });
                    }}
                    disabled={deploying || !validation.valid}
                  >
                    <span className="mkt-modal-warm-menu-label">
                      Deploy with <span className="mkt-modal-warm-menu-count">{n}</span>{' '}
                      warm worker{n === 1 ? '' : 's'}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </>
  );
};

export default MarketplaceWorkflowDeployFooter;
